import { useState, useEffect } from "react";
import { showNotification } from "../../../../store/Central/selectors";
import { useUserManagementStore } from "../../../../store/UserManagement";
import { useDesktopsStore } from "../../../../store/Desktops";
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import { Label } from "../../../../components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../../../../components/ui/select";

export const UserUpdateForm: React.FC<{
	userId: string;
	onClose: () => void;
}> = ({ userId, onClose }) => {
	const { getProfile, updateProfile, getMembers } = useUserManagementStore((state) => ({
		getProfile: state.getProfile,
		updateProfile: state.updateProfile,
		getMembers: state.getMembers,
	}));

	const locations = useDesktopsStore((state) => state.locations);

	const [code, setCode] = useState("");
	const [firstName, setFirstName] = useState("");
	const [lastName, setLastName] = useState("");
	const [locationId, setLocationId] = useState<number | null>(null);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		const loadProfile = async () => {
			const data = await getProfile(userId);

			if (!data) {
				showNotification({
					message: "Fehler beim Laden des Profils.",
					type: "error",
				});
				return;
			}

			setCode(data.code);
			setFirstName(data.first_name);
			setLastName(data.last_name);
			setLocationId(data.location_id);
			setLoading(false);
		};

		loadProfile();
	}, [userId, getProfile]);

	const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();

		const success = await updateProfile(userId, {
			code,
			first_name: firstName,
			last_name: lastName,
			location_id: locationId,
		});

		if (!success) {
			showNotification({
				message: "Fehler beim Aktualisieren des Profils.",
				type: "error",
			});
			return;
		}

		showNotification({
			message: "Profil erfolgreich aktualisiert.",
			type: "success",
		});

		await getMembers();
		onClose();
	};

	if (loading) {
		return <div className="py-4 text-center text-gray-500">Laden...</div>;
	}

	return (
		<form onSubmit={handleSubmit} className="space-y-4">
			<div className="space-y-2">
				<Label htmlFor="code">Mitarbeitercode</Label>
				<Input
					id="code"
					name="code"
					value={code}
					onChange={(e) => setCode(e.target.value)}
				/>
			</div>

			<div className="space-y-2">
				<Label htmlFor="firstName">Vorname</Label>
				<Input
					id="firstName"
					name="first_name"
					value={firstName}
					onChange={(e) => setFirstName(e.target.value)}
				/>
			</div>

			<div className="space-y-2">
				<Label htmlFor="lastName">Nachname</Label>
				<Input
					id="lastName"
					name="last_name"
					value={lastName}
					onChange={(e) => setLastName(e.target.value)}
				/>
			</div>

			{locations.length > 1 && (
				<div className="space-y-2">
					<Label htmlFor="location">Standort</Label>
					<Select
						value={locationId?.toString() ?? "default"}
						onValueChange={(val) =>
							setLocationId(val === "default" ? null : parseInt(val))
						}
					>
						<SelectTrigger
							aria-label="Standort auswählen"
							data-testid={`user-location-select-${userId}`}
						>
							<SelectValue placeholder="Standard" />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="default">
								Standard
							</SelectItem>
							{locations.map((loc) => (
								<SelectItem key={loc.id} value={loc.id.toString()}>
									{loc.name}{loc.is_default ? " (Standard)" : ""}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>
			)}

			<div className="flex justify-end gap-2 pt-4">
				<Button type="button" variant="outline" onClick={onClose}>
					Abbrechen
				</Button>
				<Button type="submit">
					Speichern
				</Button>
			</div>
		</form>
	);
};
